/** @format */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Button} from '@mui/material';

import {Trans, useTranslation} from 'react-i18next';

import {Discreteness, Period} from 'components/PeriodSelector/model';

import {useMixpanelContext} from 'contexts/MixpanelContext';
import {useCompanyContext} from 'contexts/CompanyContext';

import {CHARTS_MARGIN_LEFT, CHARTS_MARGIN_RIGHT} from 'components/charts/multiline-chart/const';

import {formatDateToString} from 'helpers/dateHelpers';

import {useUpdateDigitalCastSettingsMutation} from '../../graphql/__generated__';
import {mapGraphStats} from '../../helpers/graph-stats/mapGraphStats';
import {mapLogs} from '../../helpers/logs/mapLogs';
import {mapNotes} from '../../helpers/notes/mapNotes';
import mapChartsData from '../../mapping/mapChartsData';
import {
  AnalyticGoalFromQuery,
  DcSettingsFromQuery,
  FunnelGoalStatFromQuery,
  GraphSensorCheckFromQuery,
  GraphStatFromQuery,
  MediaplanFromQuery,
  MediaplanStatFromQuery,
} from '../../models/api';
import {ChartIds} from '../../models/chartIds';
import {SavedDashboardsList} from '../../models/savedSpaceSettings';
import {Space, SpaceId, SpaceList} from '../../models/spaces';
import {LogRecordFromQuery, NoteRecordFromQuery} from '../../types';
import {WidgetBlock} from '../../models/widgets';
import Dashboard from '../dashboard';
import defaultSpace from '../dashboard/helpers/defaultSpace';
import newSpace from '../dashboard/helpers/newSpace';
import removeSpace from '../dashboard/helpers/removeSpace';
import mapSavedSpaces from '../dashboard/spaceSaving/mapSavedSpaces';
import parseSavedSpaces from '../dashboard/spaceSaving/parseSavedSpaces';
import injectWidgetSettings from './helpers/injectWidgetSettings';
import pruneMissingFunnelWidgets from './helpers/pruneMissingFunnelWidgets';
import {DashboardWrapRoot as Root, DashboardWrapClasses as classes} from './style';
import {addCrIdsToBlocks, buildCrChartsForSpace, ensureCrSettings, getSavedCrMap} from './utils';
import {mapSensors} from '../../helpers/sensors/mapSensors';
import {StubLayerProvider, StubLayerData} from '../../contexts/StubLayerContext';
import {DTO_DATE_FORMAT} from '../../constants/common';
import {getCurrentRechartsDates} from '../../helpers/getCurrentRechartsDates';
import {calcDaysRange} from '../../helpers/scatter-discreteness/calcDaysRange';
import {mapFunnel} from '../../helpers/funnel-cr/mapFunnel';

interface Props {
  startDate: Date;
  endDate: Date;
  activePeriod: Period;
  activeDiscreteness: Discreteness;
  graphStatsDto: ReadonlyArray<GraphStatFromQuery>;
  mediaplanStatsDto: ReadonlyArray<MediaplanStatFromQuery>;
  mediaplansDto: ReadonlyArray<MediaplanFromQuery>;
  graphSensorChecks: ReadonlyArray<GraphSensorCheckFromQuery>;
  logs: ReadonlyArray<LogRecordFromQuery>;
  notes: ReadonlyArray<NoteRecordFromQuery>;
  refetchNotes: () => void;
  analyticGoals: ReadonlyArray<AnalyticGoalFromQuery>;
  funnelGoalStats: ReadonlyArray<FunnelGoalStatFromQuery>;
  digitalCastSettings: DcSettingsFromQuery;
  companyId: string;
  stubLayers: readonly StubLayerData[];
}

const DashboardWrap = ({
  startDate,
  endDate,
  activePeriod,
  activeDiscreteness,
  graphStatsDto,
  mediaplansDto,
  mediaplanStatsDto,
  graphSensorChecks,
  digitalCastSettings,
  companyId,
  analyticGoals,
  funnelGoalStats,
  logs,
  notes,
  refetchNotes,
  stubLayers,
}: Props) => {
  const spaceListParsed = parseSavedSpaces(digitalCastSettings);
  const [spaceList, setSpaceList] = useState<SpaceList | null>(spaceListParsed);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const {mixpanel} = useMixpanelContext();
  const {currentCompany} = useCompanyContext();

  const ref = useRef<HTMLDivElement>(null);

  const {t} = useTranslation();

  const [mutateDashboard] = useUpdateDigitalCastSettingsMutation({
    ignoreResults: true,
  });

  const handleMutateDashboard = useCallback(
    (savedSpaces: SavedDashboardsList | null) => {
      // При каждом изменении представления должна вызываться мутация на сохранение данных на сервер
      // Запрос не должен блокировать интерфейс

      mutateDashboard({
        variables: {
          settings: {
            settings: JSON.stringify(savedSpaces),
          },
        },
      }).catch(err => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    },
    [mutateDashboard],
  );

  useEffect(() => {
    // Если юзер зашёл первый раз и настроек ещё нет, то надо создать и сохранить дефолтное пространство
    if (!spaceList) {
      const newSpaceList = defaultSpace();
      setSpaceList(newSpaceList);
      const savedSpaces = mapSavedSpaces(newSpaceList);
      handleMutateDashboard(savedSpaces);
    }
  }, [handleMutateDashboard, spaceList]);

  // Вычисление размеров нужно, чтобы подобрать оптимальное количество кнопок в скаттер-графиках

  const handleResize = useCallback(() => {
    if (ref.current) {
      setContainerWidth(ref.current.clientWidth - CHARTS_MARGIN_LEFT - CHARTS_MARGIN_RIGHT);
    }
  }, []);

  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const saveSpaceSettings = (space: Space) => {
    const currentSpace = spaceList?.[space.id];
    if (!currentSpace) {
      return;
    }

    const crWidgetIds = Object.keys(currentSpace.widgetsSettings).filter(id =>
      id.startsWith('funnel_cr_'),
    ) as ChartIds[];

    const updatedSpace = {
      ...space,
      widgetsMenuSelected: [...new Set([...space.widgetsMenuSelected, ...crWidgetIds])],
    };

    const updatedSpaceList = {
      ...spaceList,
      [space.id]: updatedSpace,
    };
    setSpaceList(updatedSpaceList);

    const savedSpaces = mapSavedSpaces(updatedSpaceList);
    handleMutateDashboard(savedSpaces);
  };

  const handleAddSpace = () => {
    mixpanel.track('add_digital_cast_workspace', {project_uuid: currentCompany?.uuid});
    const newSpaceList = newSpace(spaceList);
    setSpaceList(newSpaceList);
    const savedSpaces = mapSavedSpaces(newSpaceList);
    handleMutateDashboard(savedSpaces);
  };

  const saveSpacesSettings = (spaces: SpaceList) => {
    const savedSpaces = mapSavedSpaces(spaces);
    handleMutateDashboard(savedSpaces);
  };

  const handleRemoveSpace = (spaceId: SpaceId) => {
    mixpanel.track('remove_digital_cast_workspace', {project_uuid: currentCompany?.uuid});
    setSpaceList(prev =>
      removeSpace({
        originalSpace: prev,
        spaceId,
        saveSpacesSettings,
      }),
    );
  };

  const handleWidgetChange = (widgetId: ChartIds) => {
    mixpanel.track('change_digital_cast_workspace_widget', {
      project_uuid: currentCompany?.uuid,
      widget_id: widgetId,
    });
  };

  const handleWorkspaceChange = () => {
    mixpanel.track('change_digital_cast_workspace', {project_uuid: currentCompany?.uuid});
  };

  const stringStartDate = formatDateToString(startDate, DTO_DATE_FORMAT);
  const stringEndDate = formatDateToString(endDate, DTO_DATE_FORMAT);

  const rechartsDates = useMemo(
    () =>
      getCurrentRechartsDates({
        startDate: stringStartDate,
        endDate: stringEndDate,
        discreteness: activeDiscreteness,
      }),
    [stringStartDate, stringEndDate, activeDiscreteness],
  );

  const {graphStats, graphComplexValues} = useMemo(
    () =>
      mapGraphStats({
        graphStats: graphStatsDto,
        mediaplanStats: mediaplanStatsDto,
        rechartsDates,
        startDate,
        endDate,
      }),
    [graphStatsDto, mediaplanStatsDto, rechartsDates, startDate, endDate],
  );

  const daysRange = useMemo(
    () => calcDaysRange(containerWidth, rechartsDates.length),
    [containerWidth, rechartsDates.length],
  );

  const mappedLogs = useMemo(
    () =>
      mapLogs({
        data: logs,
        daysRange,
        rechartsDates,
      }),
    [logs, daysRange, rechartsDates],
  );

  const mappedNotes = useMemo(
    () =>
      mapNotes({
        data: notes,
        daysRange,
        rechartsDates,
      }),
    [notes, daysRange, rechartsDates],
  );

  const sensors = useMemo(
    () => mapSensors(graphSensorChecks, rechartsDates),
    [graphSensorChecks, rechartsDates],
  );

  const mappedFunnelGoalStats = useMemo(
    () => mapFunnel(funnelGoalStats, rechartsDates),
    [funnelGoalStats, rechartsDates],
  );

  // `now` зафиксирован на mount, чтобы каждый ре-рендер не пересчитывал mapChartsData.
  // Перейти на следующий день требует перезагрузки DC — приемлемо, страница и так перезагружается.
  const nowRef = useRef(new Date());

  const {charts, blocks, widgetSettings, widgetConditions} = useMemo(
    () =>
      mapChartsData({
        mediaplansDto,
        graphStats,
        graphComplexValues,
        sensors,
        startDate: stringStartDate,
        endDate: stringEndDate,
        period: activePeriod,
        now: nowRef.current,
        companyId,
        analyticGoals,
        funnelGoalStats: mappedFunnelGoalStats,
        t,
        logs: mappedLogs,
        notes: mappedNotes,
        refetchNotes,
        rechartsDates,
        activeDiscreteness,
      }),
    [
      mediaplansDto,
      graphStats,
      graphComplexValues,
      sensors,
      stringStartDate,
      stringEndDate,
      activePeriod,
      companyId,
      analyticGoals,
      mappedFunnelGoalStats,
      t,
      mappedLogs,
      mappedNotes,
      refetchNotes,
      rechartsDates,
      activeDiscreteness,
    ],
  );

  // chartsData одинаков для всех spaces (deps не per-space). Считаем один раз.
  const sharedChartsData = useMemo(
    () =>
      buildCrChartsForSpace({
        base: charts,
        analyticGoals,
        rechartsDates,
        discreteness: activeDiscreteness,
      }),
    [charts, analyticGoals, rechartsDates, activeDiscreteness],
  );

  // Per-space computations (space + widgetBlocks). Стабильная ссылка → Dashboard не реконструирует
  // chartsData каскад, NoteButton не ремаунтится при ре-рендере DashboardWrap.
  const spacesData = useMemo(() => {
    if (!spaceList) return null;
    return Object.keys(spaceList).reduce<
      Record<string, {space: Space; widgetBlocks: WidgetBlock[]}>
    >((acc, spaceId) => {
      const space = ensureCrSettings(
        pruneMissingFunnelWidgets(
          injectWidgetSettings(spaceList[spaceId], widgetSettings),
          analyticGoals,
        ),
        getSavedCrMap(spaceId, digitalCastSettings),
        analyticGoals,
      );
      acc[spaceId] = {
        space,
        widgetBlocks: addCrIdsToBlocks(blocks, space),
      };
      return acc;
    }, {});
  }, [spaceList, widgetSettings, analyticGoals, digitalCastSettings, blocks]);

  return (
    <StubLayerProvider stubLayers={stubLayers}>
      <Root>
        {spacesData &&
          Object.keys(spacesData).map((spaceId, index) => {
            const {space, widgetBlocks} = spacesData[spaceId];
            return (
              <Dashboard
                key={spaceId}
                ref={ref}
                space={space}
                chartsData={sharedChartsData}
                widgetBlocks={widgetBlocks}
                widgetConditions={widgetConditions}
                analyticGoals={analyticGoals}
                funnelGoalStats={funnelGoalStats}
                rechartsDates={rechartsDates}
                graphComplexValues={graphComplexValues}
                saveSpaceSettings={saveSpaceSettings}
                onRemoveSpace={() => handleRemoveSpace(spaceId)}
                onWidgetChange={handleWidgetChange}
                onWorkspaceChange={handleWorkspaceChange}
                zIndex={1000 - index}
                discreteness={activeDiscreteness}
              />
            );
          })}
        <Button
          onClick={handleAddSpace}
          className={classes.addButton}
          disableRipple
          data-testid='dc-add-space-button'
        >
          <Trans>Добавить пространство</Trans>
        </Button>
      </Root>
    </StubLayerProvider>
  );
};

export default DashboardWrap;
